import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { AlertTriangle, Lightbulb, Clock, CheckCircle2 } from "lucide-react";

const GreenToClean = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Green-to-Clean Recovery" 
        description="Full pool recovery procedure for severely algae-infested pools"
      >
        <section className="space-y-8">
          {/* When to Use */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-foreground mb-3">When to Use This Procedure</h2>
            <p className="text-muted-foreground mb-4">
              Green-to-Clean is a multi-day recovery process for pools with severe algae blooms. 
              Use this when:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Pool is solid green — can't see the bottom</li>
              <li>• Standard shocking isn't clearing the water</li>
              <li>• Pool has been neglected for extended period</li>
              <li>• New client pool needs complete restoration</li>
            </ul>
          </div>

          {/* Drain vs Recover Decision */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Drain vs. Recover</h2>
            <p className="text-muted-foreground mb-4">
              Sometimes draining and refilling is more practical than recovery. Consider these factors:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                <h4 className="font-semibold text-primary mb-2">Recover (Green-to-Clean)</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Water is salvageable (not black/swamp)</li>
                  <li>• CYA is under 80 ppm</li>
                  <li>• No visible debris on bottom</li>
                  <li>• Water bill concerns</li>
                  <li>• Plaster pools (draining risks damage)</li>
                </ul>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <h4 className="font-semibold text-amber-600 mb-2">Drain & Refill</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• CYA is above 100 ppm</li>
                  <li>• Heavy debris/leaves on bottom</li>
                  <li>• Water is black or swamp-like</li>
                  <li>• TDS (total dissolved solids) is very high</li>
                  <li>• Chemical costs would exceed water cost</li>
                </ul>
              </div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Important:</strong> Always notify the office before 
                  recommending a drain. Draining requires special considerations and client approval.
                </p>
              </div>
            </div>
          </div>

          {/* Day-by-Day Process */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Clock className="h-6 w-6 text-primary" />
              Day-by-Day Process
            </h2>
            
            {/* Day 1 */}
            <div className="bg-card border-2 border-primary rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-foreground mb-4">Day 1: Assessment & Initial Shock</h3>
              <ol className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">1</span>
                  <div>
                    <strong className="text-foreground">Test water</strong>
                    <p className="text-sm">pH, chlorine, CYA, alkalinity. Document all readings.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">2</span>
                  <div>
                    <strong className="text-foreground">Remove large debris</strong>
                    <p className="text-sm">Skim leaves and anything visible. Don't stir up bottom yet.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">3</span>
                  <div>
                    <strong className="text-foreground">Lower pH to 7.2</strong>
                    <p className="text-sm">Chlorine is most effective at lower pH. Add acid as needed.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">4</span>
                  <div>
                    <strong className="text-foreground">Triple shock</strong>
                    <p className="text-sm">Target 30+ ppm chlorine. Use liquid chlorine for fastest results.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">5</span>
                  <div>
                    <strong className="text-foreground">Brush all surfaces</strong>
                    <p className="text-sm">Walls, floor, steps — break up algae so chlorine can reach it.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">6</span>
                  <div>
                    <strong className="text-foreground">Run pump 24 hours</strong>
                    <p className="text-sm">Continuous circulation is critical. Check filter pressure.</p>
                  </div>
                </li>
              </ol>
            </div>

            {/* Day 2 */}
            <div className="bg-card border rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-foreground mb-4">Day 2: Maintain & Clean Filter</h3>
              <ol className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="bg-muted text-muted-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">1</span>
                  <div>
                    <strong className="text-foreground">Test chlorine</strong>
                    <p className="text-sm">Should still be 10+ ppm. If dropped significantly, add more.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-muted text-muted-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">2</span>
                  <div>
                    <strong className="text-foreground">Clean/backwash filter</strong>
                    <p className="text-sm">Filter will be loaded with dead algae. Essential to clean.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-muted text-muted-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">3</span>
                  <div>
                    <strong className="text-foreground">Brush again</strong>
                    <p className="text-sm">Continue breaking up dead algae from surfaces.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-muted text-muted-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">4</span>
                  <div>
                    <strong className="text-foreground">Add shock if needed</strong>
                    <p className="text-sm">Maintain 10+ ppm until water clears.</p>
                  </div>
                </li>
              </ol>
              <p className="text-sm text-muted-foreground mt-4 italic">
                Expected: Water should be transitioning from green to cloudy blue/gray.
              </p>
            </div>

            {/* Day 3-4 */}
            <div className="bg-card border rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-foreground mb-4">Day 3-4: Continue Process</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Test and maintain chlorine at 10+ ppm</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Clean filter again (may need daily during recovery)</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Brush surfaces</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Keep pump running 24/7</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Once you can see bottom, vacuum to waste</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4 italic">
                Expected: Water should be clearing to cloudy/hazy. Bottom becoming visible.
              </p>
            </div>

            {/* Day 5+ */}
            <div className="bg-card border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Day 5+: Final Clearing</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Vacuum all debris to waste</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Clean filter thoroughly</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Add clarifier if water is still hazy</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Balance all chemistry once clear</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Add algaecide for prevention</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Resume normal service schedule</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4 italic">
                Expected: Crystal clear water, normal chemistry, ready for swimming.
              </p>
            </div>
          </div>

          {/* Chemical Estimates */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Chemical Requirements (Estimates)</h2>
            <div className="bg-card border rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                For a typical 20,000 gallon pool with severe green algae:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border p-3 text-left font-semibold">Chemical</th>
                      <th className="border p-3 text-left font-semibold">Amount</th>
                      <th className="border p-3 text-left font-semibold">Purpose</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr>
                      <td className="border p-3">Liquid Chlorine</td>
                      <td className="border p-3">10-15 gallons</td>
                      <td className="border p-3">Initial shock + maintenance</td>
                    </tr>
                    <tr className="bg-muted/30">
                      <td className="border p-3">Muriatic Acid</td>
                      <td className="border p-3">2-4 gallons</td>
                      <td className="border p-3">Lower pH for chlorine effectiveness</td>
                    </tr>
                    <tr>
                      <td className="border p-3">Algaecide</td>
                      <td className="border p-3">1 quart</td>
                      <td className="border p-3">Final prevention treatment</td>
                    </tr>
                    <tr className="bg-muted/30">
                      <td className="border p-3">Clarifier</td>
                      <td className="border p-3">As needed</td>
                      <td className="border p-3">Final clearing if hazy</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Note: Actual amounts vary based on pool size, severity, and water chemistry. Document all usage.
              </p>
            </div>
          </div>

          {/* Client Communication */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Client Communication</h2>
            <div className="bg-card border rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                Key points to communicate to the client:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• This is a <strong className="text-foreground">multi-day process</strong> — not instant</li>
                <li>• Pool will look worse before it looks better (dead algae = cloudier)</li>
                <li>• <strong className="text-foreground">No swimming</strong> until process is complete and chlorine drops</li>
                <li>• May require <strong className="text-foreground">multiple visits</strong> and additional chemical costs</li>
                <li>• Prevention is key going forward to avoid recurrence</li>
              </ul>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Pro Tip:</strong> Take before/during/after photos. 
                  This documents the work for the client and helps justify the cost of recovery.
                </p>
              </div>
            </div>
          </div>

          {/* Documentation */}
          <div className="bg-card border-2 border-primary rounded-lg p-6">
            <h3 className="text-lg font-semibold text-primary mb-3">Documentation Requirements</h3>
            <p className="text-muted-foreground mb-4">
              For Green-to-Clean jobs, document in the app:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Before photos (day 1)</li>
              <li>• All test readings each visit</li>
              <li>• All chemicals added with quantities</li>
              <li>• Progress photos each visit</li>
              <li>• Final clear photos</li>
              <li>• Total time spent on recovery</li>
            </ul>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default GreenToClean;